/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import { createContext, use, useCallback, useMemo, useState, type ReactNode } from "react"

import { Button, InlineAlert, Textarea } from "~/components/design-system/components"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import {
  DialogBodyV2,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
} from "~/components/reusable/Dialog"
import {
  useFlagPanelData,
  useInvalidateFlagPanel,
} from "~/features/feature-flags/client/useFlagPanelData"
import type { FlagPanel } from "~/features/feature-flags/client/model/flagPanel"
import {
  describeChange,
  type ChangeDescription,
  type ChangeRequest,
} from "~/features/feature-flags/client/model/changeRequest"
import { api } from "~/utils/api"

const PanelContext = createContext<FlagPanel | null>(null)
const RequestChangeContext = createContext<((request: ChangeRequest) => void) | null>(null)

function readContext<T>(value: T | null) {
  if (!value) throw new Error("Flag panel hooks need a FlagPanelProvider above them")
  return value
}

/** The panel the flag pages read, computed once for everything below. */
export const useFlagPanel = () => readContext(use(PanelContext))

/** Asks for a flag change: the provider's change dialog confirms and applies it. */
export const useRequestFlagChange = () => readContext(use(RequestChangeContext))

/** Each company or user the change reaches. */
function TargetList({ targets }: { targets: ChangeDescription["targets"] }) {
  return (
    <ul className="flex max-h-60 flex-col gap-1 overflow-y-auto">
      {targets.map((target) => (
        <li key={target.id} className="truncate label-base text-fg-base">
          {target.name}
        </li>
      ))}
    </ul>
  )
}

/** The default dialog keeps focus off its first checkbox. */
const keepFocusOff = (event: Event) => event.preventDefault()

/**
 * Confirms one flag change, with an optional note. Described from the panel as it is while open
 * (`describeChange`), so a refetch updates what it says and what it sends. Keyed per opening: the
 * note and override choices start fresh each time.
 */
function FlagChangeDialog({
  request,
  open,
  onClose,
}: {
  request: ChangeRequest
  open: boolean
  onClose: () => void
}) {
  const panel = useFlagPanel()
  const [note, setNote] = useState("")
  const invalidatePanel = useInvalidateFlagPanel()
  const mutation = api.featureFlagsPanel.applyChanges.useMutation()
  const change = useMemo(() => describeChange(panel, request), [panel, request])

  // Closes only once the server has stored it; a failure shows inline through `mutation.error`.
  const submit = () =>
    mutation.mutate(
      { ...change.batch, note: note.trim() || null },
      {
        onSuccess: () => {
          onClose()
          void invalidatePanel()
        },
        onError: () => void invalidatePanel(),
      },
    )

  return (
    <DialogRoot open={open} onOpenChange={(next) => !next && !mutation.isPending && onClose()}>
      <DialogContentV2
        className="max-w-xl"
        onOpenAutoFocus={request.kind === "default" ? keepFocusOff : undefined}
      >
        <DialogHeaderV2>
          <DialogTitle>{change.title}</DialogTitle>
        </DialogHeaderV2>
        <DialogBodyV2>
          {change.targets.length > 1 && <TargetList targets={change.targets} />}
          {change.warning && <InlineAlert tone="warning" description={change.warning} />}
          {change.blocker && <InlineAlert tone="warning" description={change.blocker} />}
          <Textarea
            small
            id="flag-change-note"
            label="Note (optional)"
            placeholder="Why this changes, for the change history"
            value={note}
            maxLength={500}
            onChange={(event) => setNote(event.target.value)}
          />
          {mutation.error && (
            <InlineAlert tone="destructive" description={mutation.error.message} />
          )}
        </DialogBodyV2>
        <DialogFooterV2 className="justify-end gap-2">
          <Button variant="neutral" size="sm" disabled={mutation.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            requires="staff"
            allowedTitle={change.blocker ?? undefined}
            disabled={!!change.blocker}
            loading={mutation.isPending}
            onClick={submit}
          >
            {change.applyLabel}
          </Button>
        </DialogFooterV2>
      </DialogContentV2>
    </DialogRoot>
  )
}

type Opened = { id: number; request: ChangeRequest; open: boolean }

/**
 * Hosts the one change dialog the flag pages share: `useRequestFlagChange()(request)` asks for
 * confirmation in it. The last request stays mounted while the dialog closes, so its content
 * doesn't blank during the exit animation; the next request starts a fresh dialog.
 */
function ReadyProvider({ panel, children }: { panel: FlagPanel; children: ReactNode }) {
  const [opened, setOpened] = useState<Opened | null>(null)
  const open = useCallback(
    (request: ChangeRequest) =>
      setOpened((current) => ({ id: (current?.id ?? 0) + 1, request, open: true })),
    [],
  )
  const close = useCallback(
    () => setOpened((current) => current && { ...current, open: false }),
    [],
  )
  return (
    <PanelContext value={panel}>
      <RequestChangeContext value={open}>
        {children}
        {opened && (
          <FlagChangeDialog
            key={opened.id}
            request={opened.request}
            open={opened.open}
            onClose={close}
          />
        )}
      </RequestChangeContext>
    </PanelContext>
  )
}

/** Loads the flag panel and hosts the one change dialog its descendants share. Renders the panel
 * only once the environment is known. */
export function FlagPanelProvider({ children }: { children: ReactNode }) {
  const data = useFlagPanelData()
  switch (data.status) {
    case "pending":
      return <LoadingOverlay messages="Loading feature flags" height="h-full" />
    case "error":
      return <InlineAlert tone="destructive" description={data.error.message} />
    case "ready":
      return <ReadyProvider panel={data.panel}>{children}</ReadyProvider>
  }
}
